type LogLevel = 'error' | 'warn' | 'info' | 'debug';

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const consoleMap: Record<LogLevel, (...args: unknown[]) => void> = {
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
  debug: console.debug ? console.debug.bind(console) : console.log.bind(console),
};

const normalizeLevel = (value?: string | null): LogLevel | null => {
  if (!value) {
    return null;
  }
  const normalized = value.toLowerCase();
  if (['error', 'warn', 'info', 'debug'].includes(normalized)) {
    return normalized as LogLevel;
  }
  return null;
};

const resolvedLevel: LogLevel =
  normalizeLevel(process.env.REACT_APP_LOG_LEVEL) ||
  (process.env.NODE_ENV === 'production' ? 'warn' : 'info');

const shouldLog = (level: LogLevel) =>
  LEVEL_PRIORITY[level] <= LEVEL_PRIORITY[resolvedLevel];

const formatArgs = (scope: string, args: unknown[]) => {
  if (!args.length) {
    return [`[${scope}]`];
  }
  return [`[${scope}]`, ...args];
};

export interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

export const createLogger = (scope: string): Logger => ({
  debug: (...args: unknown[]) => {
    if (shouldLog('debug')) {
      consoleMap.debug(...formatArgs(scope, args));
    }
  },
  info: (...args: unknown[]) => {
    if (shouldLog('info')) {
      consoleMap.info(...formatArgs(scope, args));
    }
  },
  warn: (...args: unknown[]) => {
    if (shouldLog('warn')) {
      consoleMap.warn(...formatArgs(scope, args));
    }
  },
  error: (...args: unknown[]) => {
    consoleMap.error(...formatArgs(scope, args));
  },
});

export const appLogger = createLogger('app');
